import { useEffect, useState } from 'react';

const TEAM_FONT_URLS: Record<string, string> = {
  Amiri: 'https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap',
  'Cormorant Garamond':
    'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&display=swap',
  'Noto Serif JP':
    'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;600;700&display=swap',
  'Noto Serif KR':
    'https://fonts.googleapis.com/css2?family=Noto+Serif+KR:wght@400;700&display=swap',
  Oswald: 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&display=swap',
  'Playfair Display':
    'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&display=swap',
};

/**
 * Inject the Google-Fonts stylesheet for a per-team display font into
 * <head>. Idempotent — duplicate calls reuse the existing <link>. Loads
 * eagerly (no requestIdleCallback): the App-level loading gate waits on
 * `document.fonts.check()` for this family before rendering, so deferring
 * here just delays the gate.
 */
export function loadTeamFontStylesheet(
  fontFamily: string | null | undefined,
): void {
  if (typeof document === 'undefined' || !fontFamily) return;
  const href = TEAM_FONT_URLS[fontFamily];
  if (!href) return;
  if (document.querySelector(`link[data-team-font="${fontFamily}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.teamFont = fontFamily;
  document.head.appendChild(link);
}

// Visible-text fonts the loading gate must wait on. Inter covers all body
// copy; Bebas Neue covers headlines when no team theme is active and is
// also the first fallback in `--team-font`'s stack. Mono is omitted: it's
// only used for tabular numerics (scores, points), which fall back to a
// system mono cleanly enough that the swap-in isn't perceptible.
const BASE_FONTS = ['Inter', 'Bebas Neue'] as const;

async function ensureFontLoaded(family: string, deadline: number): Promise<void> {
  // `document.fonts.load()` resolves immediately with `[]` when the family
  // isn't yet registered (the index.html stylesheet hasn't applied yet),
  // so a single call can lie. Poll until `check()` confirms the binary is
  // available, or we hit the deadline. The CSS fallback chain produces
  // legible text past the deadline, so we never trap the gate.
  while (performance.now() < deadline) {
    try {
      await document.fonts.load(`1em "${family}"`);
    } catch {
      // Some browsers throw on certain family-name shapes; treat as a miss.
    }
    if (document.fonts.check(`1em "${family}"`)) return;
    await new Promise((r) => setTimeout(r, 60));
  }
}

/**
 * Returns true once the base UI fonts (Inter + Bebas Neue) AND the
 * optional per-team display font are loaded — i.e. when text painted on
 * the App shell will use its final glyphs without a swap-in flicker.
 *
 * Implementation notes:
 * - Resets to `false` whenever `teamFont` changes (initial mount or the
 *   user switching favorite team), then flips back to `true` when the
 *   new font is ready. The "ready" state is keyed on the teamFont value
 *   so a team change immediately invalidates readiness without one
 *   stale-paint frame.
 * - Falls through after 2.5s as a safety net so a missing/blocked Google
 *   Fonts request never traps the app behind the spinner.
 * - No-op (returns true immediately) on non-browser environments / when
 *   `document.fonts` is unavailable.
 */
export function useFontsReady(teamFont: string | null | undefined): boolean {
  const [state, setState] = useState<{ for: string | null | undefined } | null>(
    null,
  );

  useEffect(() => {
    if (typeof document === 'undefined' || typeof document.fonts === 'undefined') {
      setState({ for: teamFont });
      return;
    }
    let cancelled = false;
    if (teamFont) loadTeamFontStylesheet(teamFont);
    const families = [...BASE_FONTS, ...(teamFont ? [teamFont] : [])];
    const deadline = performance.now() + 2500;
    Promise.all(families.map((f) => ensureFontLoaded(f, deadline))).then(() => {
      if (!cancelled) setState({ for: teamFont });
    });
    return () => {
      cancelled = true;
    };
  }, [teamFont]);

  // Keying the ready signal on the teamFont value means a team change
  // invalidates readiness in the SAME render that teamFont updates — no
  // intermediate frame where ready=true but the font is wrong.
  return state !== null && state.for === teamFont;
}
